/**
 * Notification Center Component
 * Handles notification preferences, in-app notifications, and test notifications
 */

import React, { useState, useEffect, useRef } from 'react';

const NotificationCenter = ({ apiService }) => {
  // State management
  const [activeTab, setActiveTab] = useState('notifications');
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState({
    email: '',
    webhook_url: '',
    slack_webhook: '',
    teams_webhook: '',
    channels: ['email', 'in_app'],
    mcp_detection_threshold: 3,
    iso_compliance_alerts: true,
    immediate_notifications: false,
    daily_digest: true
  });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);
  const intervalRef = useRef();

  // Load initial data
  useEffect(() => {
    loadInitialData();

    // Set up auto-refresh for notifications every 30 seconds
    intervalRef.current = setInterval(() => {
      if (activeTab === 'notifications') {
        loadNotifications();
      }
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      await Promise.all([
        loadNotifications(),
        loadPreferences(),
        loadStats()
      ]);

    } catch (err) {
      setError('Failed to load notification data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const data = await apiService.getNotifications(50, false);
      setNotifications(data || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  const loadPreferences = async () => {
    try {
      const data = await apiService.getNotificationPreferences();
      setPreferences(data);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const loadStats = async () => {
    try {
      const data = await apiService.getNotificationStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChannelToggle = (channel) => {
    setPreferences(prev => {
      const channels = prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel];

      return { ...prev, channels };
    });
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      setError('');

      await apiService.setNotificationPreferences(preferences);
      setSuccess('Notification preferences saved successfully!');

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save preferences: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await apiService.markNotificationRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );

      // Update stats
      await loadStats();
    } catch (err) {
      setError('Failed to mark notification as read: ' + err.message);
    }
  };

  const sendTestNotification = async (type) => {
    try {
      setTestingNotification(true);
      setError('');

      await apiService.sendTestNotification(type, `Test ${type} notification from Notification Center`);
      setSuccess(`Test ${type} notification sent successfully!`);

      // Reload notifications to show the test notification
      setTimeout(() => {
        loadNotifications();
        loadStats();
      }, 2000);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to send test notification: ' + err.message);
    } finally {
      setTestingNotification(false);
    }
  };

  const getNotificationIcon = (type, priority) => {
    const icons = {
      mcp_detection: '🔍',
      compliance_scan: '📊',
      threat_model: '🚨',
      system: '⚙️'
    };

    const priorityColors = {
      critical: '#dc2626',
      high: '#ea580c',
      medium: '#d97706',
      low: '#65a30d'
    };

    return (
      <span style={{ color: priorityColors[priority] || '#6b7280' }}>
        {icons[type] || '📢'}
      </span>
    );
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch (err) {
      return 'Unknown time';
    }
  };

  if (loading) {
    return (
      <div className="notification-center loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-center">
      <div className="notification-center-header">
        <h1>🔔 Notification Center</h1>
        <div className="notification-stats">
          <div className="stat">
            <span className="stat-value">{stats.total_notifications || 0}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat">
            <span className="stat-value">{stats.unread_count || 0}</span>
            <span className="stat-label">Unread</span>
          </div>
        </div>
      </div>

      <div className="notification-center-tabs">
        <button
          className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          📬 Notifications
          {stats.unread_count > 0 && (
            <span className="unread-badge">{stats.unread_count}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          ⚙️ Settings
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="notifications-list">
          <div className="notifications-header">
            <h2>Recent Notifications</h2>
            <button
              className="btn-secondary"
              onClick={() => loadNotifications()}
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>

          {notifications.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔔</div>
              <h3>No notifications yet</h3>
              <p>You'll see MCP detection alerts and compliance updates here.</p>
            </div>
          ) : (
            <div className="notifications-container">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type, notification.priority)}
                  </div>

                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatTimestamp(notification.created_at)}
                      </span>
                      <span className={`notification-priority priority-${notification.priority}`}>
                        {notification.priority}
                      </span>
                    </div>
                  </div>

                  <div className="notification-actions">
                    {!notification.read && (
                      <button
                        className="btn-link"
                        onClick={() => markAsRead(notification.id)}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'preferences' && (
        <div className="notification-preferences">
          <div className="preferences-section">
            <h2>Notification Channels</h2>
            <p>Choose how you want to receive alerts about MCP detections and compliance issues.</p>

            <div className="channels-grid">
              {[
                { key: 'email', label: '📧 Email', description: 'Receive alerts via email' },
                { key: 'in_app', label: '🔔 In-App', description: 'Show alerts in this dashboard' },
                { key: 'webhook', label: '🔗 Webhook', description: 'Send to custom webhook URL' },
                { key: 'slack', label: '💬 Slack', description: 'Post to Slack channel' },
                { key: 'teams', label: '💼 Teams', description: 'Post to Microsoft Teams' }
              ].map((channel) => (
                <div key={channel.key} className="channel-option">
                  <label className="channel-label">
                    <input
                      type="checkbox"
                      checked={preferences.channels.includes(channel.key)}
                      onChange={() => handleChannelToggle(channel.key)}
                    />
                    <span className="channel-text">
                      <strong>{channel.label}</strong>
                      <small>{channel.description}</small>
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="preferences-section">
            <h2>Contact Information</h2>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={preferences.email || ''}
                onChange={(e) => handlePreferenceChange('email', e.target.value)}
                placeholder="your.email@example.com"
                disabled={!preferences.channels.includes('email')}
              />
            </div>

            <div className="form-group">
              <label>Webhook URL</label>
              <input
                type="url"
                value={preferences.webhook_url || ''}
                onChange={(e) => handlePreferenceChange('webhook_url', e.target.value)}
                placeholder="https://your-webhook.com/alerts"
                disabled={!preferences.channels.includes('webhook')}
              />
            </div>

            <div className="form-group">
              <label>Slack Webhook URL</label>
              <input
                type="url"
                value={preferences.slack_webhook || ''}
                onChange={(e) => handlePreferenceChange('slack_webhook', e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                disabled={!preferences.channels.includes('slack')}
              />
            </div>

            <div className="form-group">
              <label>Teams Webhook URL</label>
              <input
                type="url"
                value={preferences.teams_webhook || ''}
                onChange={(e) => handlePreferenceChange('teams_webhook', e.target.value)}
                placeholder="https://outlook.office.com/webhook/..."
                disabled={!preferences.channels.includes('teams')}
              />
            </div>
          </div>

          <div className="preferences-section">
            <h2>Alert Settings</h2>

            <div className="form-group">
              <label>MCP Detection Threshold</label>
              <input
                type="number"
                min="1"
                max="100"
                value={preferences.mcp_detection_threshold}
                onChange={(e) => handlePreferenceChange('mcp_detection_threshold', parseInt(e.target.value))}
              />
              <small>Minimum number of MCP detections to trigger an alert</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={preferences.iso_compliance_alerts}
                  onChange={(e) => handlePreferenceChange('iso_compliance_alerts', e.target.checked)}
                />
                Enable ISO 42001 compliance alerts
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={preferences.immediate_notifications}
                  onChange={(e) => handlePreferenceChange('immediate_notifications', e.target.checked)}
                />
                Send immediate notifications (vs batching)
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={preferences.daily_digest}
                  onChange={(e) => handlePreferenceChange('daily_digest', e.target.checked)}
                />
                Enable daily digest emails
              </label>
            </div>
          </div>

          <div className="preferences-section">
            <h2>Test Notifications</h2>
            <p>Send test notifications to verify your configuration.</p>

            <div className="test-buttons">
              {preferences.channels.map((channel) => (
                <button
                  key={channel}
                  className="btn-secondary"
                  onClick={() => sendTestNotification(channel)}
                  disabled={testingNotification}
                >
                  {testingNotification ? '⏳' : '🧪'} Test {channel}
                </button>
              ))}
            </div>
          </div>

          <div className="preferences-actions">
            <button
              className="btn-primary"
              onClick={savePreferences}
              disabled={saving}
            >
              {saving ? '⏳ Saving...' : '💾 Save Preferences'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;